import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Global, keyframes } from '@emotion/react';
import {
  Alert,
  AlertIcon,
  AspectRatio,
  Badge,
  Box,
  Button,
  Collapse,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  FiArrowLeft,
  FiMaximize,
  FiMinimize,
  FiPlay,
  FiPlusCircle,
  FiRotateCw,
  FiSettings,
  FiTool,
  FiDownloadCloud,
  FiEye,
  FiEyeOff,
  FiStar,
  FiXCircle,
} from 'react-icons/fi';
import { FaGamepad, FaGem, FaMoneyBillWave, FaHandHoldingUsd } from 'react-icons/fa';
import { GiRollingDices, GiTwoCoins } from 'react-icons/gi';
import type { IconType } from 'react-icons';
import {
  CordeCommonModelsDtoMachineGameSession,
  CordeCommonModelsDtoMachineMachine,
} from '@/api/generated/v1/api';
import { useMachineService } from '../../hooks/useMachineService';
import { useBalanceService } from '@/hooks/useBalanceService';
import { useUserProfile } from '@/contexts/userprofile/UserProfileContext';
import { useVideoStream } from '@/hooks/useVideoStream';
import { useInputEventsStream } from '@/hooks/useInputEventsStream';
import { useFavorites } from '../../hooks/useFavorites';
import { ButtonAction, TouchAction } from '@/types/websocket';

const MAIN_ELEMENT_ID = 'playCabinetMainStream';
const GENERAL_ELEMENT_ID = 'playCabinetGeneralStream';

// This is a live realtime stream, not a recording — strip every native media
// control (play/pause overlay, start-playback button, panel) from the videos.
const HIDE_NATIVE_VIDEO_CONTROLS = `
  #${MAIN_ELEMENT_ID}::-webkit-media-controls,
  #${GENERAL_ELEMENT_ID}::-webkit-media-controls,
  #${MAIN_ELEMENT_ID}::-webkit-media-controls-enclosure,
  #${GENERAL_ELEMENT_ID}::-webkit-media-controls-enclosure,
  #${MAIN_ELEMENT_ID}::-webkit-media-controls-panel,
  #${GENERAL_ELEMENT_ID}::-webkit-media-controls-panel,
  #${MAIN_ELEMENT_ID}::-webkit-media-controls-play-button,
  #${GENERAL_ELEMENT_ID}::-webkit-media-controls-play-button,
  #${MAIN_ELEMENT_ID}::-webkit-media-controls-overlay-play-button,
  #${GENERAL_ELEMENT_ID}::-webkit-media-controls-overlay-play-button,
  #${MAIN_ELEMENT_ID}::-webkit-media-controls-start-playback-button,
  #${GENERAL_ELEMENT_ID}::-webkit-media-controls-start-playback-button {
    display: none !important;
    -webkit-appearance: none !important;
    opacity: 0 !important;
  }
`;

// Physical slot buttons, grouped like a real cabinet. IDs match the machine-proxy contract.
const MAIN_BUTTONS: Array<{ id: number; key: string; icon: IconType; color: string }> = [
  { id: 0, key: 'start', icon: FiPlay, color: 'red' },
  { id: 1, key: 'auto', icon: FiRotateCw, color: 'cyan' },
  { id: 2, key: 'game', icon: FaGamepad, color: 'purple' },
  { id: 4, key: 'cashout', icon: FaMoneyBillWave, color: 'green' },
  { id: 5, key: 'take', icon: FaHandHoldingUsd, color: 'teal' },
  { id: 6, key: 'max', icon: FaGem, color: 'orange' },
];
const SERVICE_BUTTONS: Array<{ id: number; key: string; icon: IconType; color: string }> = [
  { id: 3, key: 'service', icon: FiSettings, color: 'gray' },
  { id: 7, key: 'bet', icon: GiTwoCoins, color: 'blue' },
  { id: 8, key: 'servmode', icon: FiTool, color: 'gray' },
];
const LINE_IDS = [10, 11, 12, 13, 14];
const BET_IDS = [15, 16, 17, 18, 19];

// Glossy radial fills + neon glow for each casino-button color.
const CASINO_PALETTE: Record<string, { grad: string; glow: string }> = {
  red: { grad: 'radial-gradient(circle at 35% 28%, #ff9aa2, #e11d3c 55%, #7c0b1c)', glow: 'rgba(255,45,85,0.6)' },
  green: { grad: 'radial-gradient(circle at 35% 28%, #9dffbf, #16b35a 55%, #0a6b38)', glow: 'rgba(38,224,122,0.55)' },
  teal: { grad: 'radial-gradient(circle at 35% 28%, #8ff5e6, #12a89a 55%, #0a6d63)', glow: 'rgba(26,224,205,0.55)' },
  cyan: { grad: 'radial-gradient(circle at 35% 28%, #9ce9ff, #12a5c9 55%, #0a5f77)', glow: 'rgba(38,200,240,0.55)' },
  purple: { grad: 'radial-gradient(circle at 35% 28%, #d9a6ff, #8a2be2 55%, #4a148c)', glow: 'rgba(170,90,255,0.6)' },
  blue: { grad: 'radial-gradient(circle at 35% 28%, #a6c8ff, #2b6cff 55%, #143a8f)', glow: 'rgba(58,110,255,0.55)' },
  orange: { grad: 'radial-gradient(circle at 35% 28%, #ffd08a, #f7801e 55%, #a34d00)', glow: 'rgba(255,140,30,0.6)' },
  yellow: { grad: 'radial-gradient(circle at 35% 28%, #fff1a6, #f5c518 55%, #a3820a)', glow: 'rgba(245,197,24,0.55)' },
  gray: { grad: 'radial-gradient(circle at 35% 28%, #cfd6df, #7b8794 55%, #434c56)', glow: 'rgba(180,190,200,0.4)' },
};

const livePulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

const normStatus = (status?: string): string => (status || '').toUpperCase();
const isPlayable = (status?: string): boolean => {
  const s = normStatus(status);
  return s === 'FREE' || s === 'BUSY' || s === 'AVAILABLE' || s === 'OCCUPIED';
};

// Responsive dimensions per button role (scales down on phones).
const BTN_DIMS = {
  main: { box: { base: '58px', sm: '70px', md: '82px' }, icon: { base: 5, sm: 6, md: 7 }, font: { base: 'lg', sm: 'xl', md: '2xl' } },
  service: { box: { base: '54px', md: '66px' }, icon: { base: 5, md: 6 }, font: { base: 'md', md: 'lg' } },
  small: { box: { base: '46px', sm: '52px', md: '58px' }, icon: { base: 4, md: 5 }, font: { base: 'sm', md: 'md' } },
} as const;

// Tactile, glowing casino button (round). Works with mouse + touch via pointer events.
// Pass `icon` for a react-icon glyph or `text` for the numbered line/bet buttons.
const CasinoButton: React.FC<{
  icon?: IconType;
  text?: string;
  label: string;
  color: string;
  variant?: keyof typeof BTN_DIMS;
  isDisabled: boolean;
  isPressed: boolean;
  onPress: () => void;
  onRelease: () => void;
}> = ({ icon, text, label, color, variant = 'main', isDisabled, isPressed, onPress, onRelease }) => {
  const p = CASINO_PALETTE[color] || CASINO_PALETTE.gray;
  const dims = BTN_DIMS[variant];
  return (
    <VStack spacing={1.5} opacity={isDisabled ? 0.4 : 1} transition="opacity 0.2s">
      <Box
        as="button"
        type="button"
        aria-label={label}
        disabled={isDisabled}
        w={dims.box}
        h={dims.box}
        borderRadius="full"
        bgImage={p.grad}
        border="3px solid"
        borderColor="whiteAlpha.500"
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
        transform={isPressed ? 'translateY(3px) scale(0.96)' : 'translateY(0)'}
        transition="transform 0.08s, filter 0.15s"
        boxShadow={
          isPressed
            ? `inset 0 6px 16px rgba(0,0,0,0.55), 0 0 12px ${p.glow}`
            : `0 6px 0 rgba(0,0,0,0.45), 0 8px 22px ${p.glow}`
        }
        _hover={!isDisabled ? { filter: 'brightness(1.12)' } : undefined}
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        onPointerDown={(e: React.PointerEvent) => {
          e.preventDefault();
          if (!isDisabled) onPress();
        }}
        onPointerUp={(e: React.PointerEvent) => {
          e.preventDefault();
          if (!isDisabled) onRelease();
        }}
        onPointerLeave={() => {
          if (isPressed) onRelease();
        }}
        onPointerCancel={() => {
          if (isPressed) onRelease();
        }}
        sx={{ WebkitTapHighlightColor: 'transparent', touchAction: 'none' }}
      >
        {icon ? (
          <Icon
            as={icon}
            boxSize={dims.icon}
            color="white"
            zIndex={1}
            filter="drop-shadow(0 1px 2px rgba(0,0,0,0.5))"
          />
        ) : (
          <Text
            fontSize={dims.font}
            fontWeight="extrabold"
            color="white"
            zIndex={1}
            filter="drop-shadow(0 1px 2px rgba(0,0,0,0.5))"
          >
            {text}
          </Text>
        )}
        {/* glossy top highlight */}
        <Box
          position="absolute"
          top="10%"
          left="20%"
          right="28%"
          h="26%"
          borderRadius="full"
          bg="whiteAlpha.500"
          filter="blur(3px)"
          pointerEvents="none"
        />
      </Box>
      <Text
        fontSize="2xs"
        fontWeight="bold"
        letterSpacing="0.06em"
        color="whiteAlpha.900"
        textTransform="uppercase"
        textAlign="center"
      >
        {label}
      </Text>
    </VStack>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <HStack w="full" align="center" spacing={3} py={1}>
    <Box flex={1} h="1px" bgGradient="linear(to-r, transparent, whiteAlpha.400)" />
    <Text
      fontSize="xs"
      fontWeight="bold"
      letterSpacing="0.22em"
      textTransform="uppercase"
      color="yellow.300"
      textShadow="0 0 10px rgba(245,197,24,0.5)"
      whiteSpace="nowrap"
    >
      {children}
    </Text>
    <Box flex={1} h="1px" bgGradient="linear(to-l, transparent, whiteAlpha.400)" />
  </HStack>
);

// Machine screen wrapped in a glowing bezel, like a real cabinet monitor.
const ScreenBezel: React.FC<{ glow: string; children: React.ReactNode }> = ({ glow, children }) => (
  <Box
    p="10px"
    borderRadius="2xl"
    bgGradient="linear(to-b, #3a2a5a, #140a24)"
    boxShadow={`0 0 44px ${glow}, inset 0 0 0 2px rgba(255,255,255,0.06)`}
  >
    <Box bg="black" borderRadius="xl" overflow="hidden" position="relative">
      {children}
    </Box>
  </Box>
);

export const PlayCabinetPage: React.FC = () => {
  const { machineId } = useParams<{ machineId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const machineService = useMachineService();
  const balanceService = useBalanceService();
  const { profile } = useUserProfile();
  const { isFavorite, toggleFavorite } = useFavorites();

  const [machine, setMachine] = useState<CordeCommonModelsDtoMachineMachine | null>(null);
  const [session, setSession] = useState<CordeCommonModelsDtoMachineGameSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [isTouching, setIsTouching] = useState(false);
  const [showOverview, setShowOverview] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pressedButtons, setPressedButtons] = useState<Record<number, boolean>>({});
  const stageRef = useRef<HTMLDivElement>(null);

  const [availableCurrencies, setAvailableCurrencies] = useState<string[]>([]);
  const [currency, setCurrency] = useState('');
  const [topUpAmount, setTopUpAmount] = useState(10);
  const [cashOutAmount, setCashOutAmount] = useState(0);
  const balancesRef = useRef<any[]>([]);

  const { isOpen: isTopUpOpen, onOpen: onTopUpOpen, onClose: onTopUpClose } = useDisclosure();
  const { isOpen: isCashOutOpen, onOpen: onCashOutOpen, onClose: onCashOutClose } = useDisclosure();

  const inputStream = useInputEventsStream({
    machineId: machineId || '',
    sessionId: session?.id || '',
    autoReconnect: true,
    maxReconnectAttempts: 5,
    reconnectDelay: 3000,
  });

  const isSecureStream = useMemo(() => {
    const url = machine?.stream_url || 'wss://ghost-wheel.ru:50101';
    return url.toLowerCase().startsWith('wss://');
  }, [machine?.stream_url]);

  const buildStreamUrl = (path: string) =>
    machine?.stream_url ? `${machine.stream_url}${path}` : `wss://ghost-wheel.ru:50140${path}`;

  const mainStreamUrl = useMemo(() => buildStreamUrl('/stream/main'), [machine?.stream_url]);
  const generalStreamUrl = useMemo(() => buildStreamUrl('/stream/general'), [machine?.stream_url]);

  const mainStream = useVideoStream({
    wsUrl: mainStreamUrl,
    videoElementId: MAIN_ELEMENT_ID,
    autoReconnect: true,
    maxReconnectAttempts: 5,
    reconnectDelay: 3000,
    tlsConfig: isSecureStream ? { enabled: true } : undefined,
  });

  const generalStream = useVideoStream({
    wsUrl: generalStreamUrl,
    videoElementId: GENERAL_ELEMENT_ID,
    autoReconnect: true,
    maxReconnectAttempts: 5,
    reconnectDelay: 3000,
    tlsConfig: isSecureStream ? { enabled: true } : undefined,
  });

  const loadMachine = async () => {
    if (!machineId) return;
    try {
      setLoading(true);
      const data = await machineService.getMachine(machineId);
      setMachine(data);
    } catch (error) {
      console.error('Failed to load machine:', error);
      toast({ title: t('play.cabinet.loadError'), status: 'error', duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMachine();
    (async () => {
      try {
        const balances = await balanceService.getBalances();
        balancesRef.current = balances;
        const cur = balances.map((b: any) => b.currency).filter(Boolean);
        setAvailableCurrencies(cur);
        if (cur.length && !currency) setCurrency(cur[0]);
      } catch (e) {
        console.warn('Failed to load balances for currency list', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineId]);

  // Main camera follows the session lifecycle.
  useEffect(() => {
    if (session && machine) {
      const timer = setTimeout(() => {
        if (!mainStream.status.connected) mainStream.startStream();
      }, 800);
      return () => clearTimeout(timer);
    }
    mainStream.stopStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, machine]);

  // Overview camera only runs while the panel is shown (saves bandwidth when hidden).
  useEffect(() => {
    if (session && machine && showOverview) {
      const timer = setTimeout(() => {
        if (!generalStream.status.connected) generalStream.startStream();
      }, 800);
      return () => clearTimeout(timer);
    }
    generalStream.stopStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, machine, showOverview]);

  // Custom (pseudo) fullscreen: Escape to exit, lock body scroll while open.
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isFullscreen]);

  // Leaving a session drops us out of fullscreen.
  useEffect(() => {
    if (!session && isFullscreen) setIsFullscreen(false);
  }, [session, isFullscreen]);

  const getAvailableBalance = (cur: string): number => {
    const bal = balancesRef.current.find((b: any) => b.currency === cur);
    if (!bal) return 0;
    return (bal.available?.units || 0) + (bal.available?.nanos || 0) / 1e9;
  };

  const handleStartSession = async () => {
    if (!machine?.id) return;
    if (!profile?.id) {
      toast({ title: t('play.cabinet.profileRequired'), status: 'error', duration: 4000, isClosable: true });
      return;
    }
    try {
      setSessionLoading(true);
      const sessionData = await machineService.reserveMachine(machine.id, profile.id);
      setSession(sessionData);
      toast({ title: t('play.cabinet.sessionStarted'), status: 'success', duration: 3000, isClosable: true });
    } catch (error) {
      console.error('Failed to start session:', error);
      toast({
        title: t('play.cabinet.sessionError'),
        description: error instanceof Error ? error.message : undefined,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSessionLoading(false);
    }
  };

  const handleEndSession = async () => {
    if (!session?.id) return;
    try {
      setSessionLoading(true);
      await machineService.releaseMachine(session.id);
      setSession(null);
      toast({ title: t('play.cabinet.sessionEnded'), status: 'success', duration: 3000, isClosable: true });
    } catch (error) {
      console.error('Failed to end session:', error);
      toast({ title: t('play.cabinet.sessionError'), status: 'error', duration: 3000, isClosable: true });
    } finally {
      setSessionLoading(false);
    }
  };

  const handleTopUp = async () => {
    if (!session?.id) return;
    try {
      const amount = { units: Math.floor(topUpAmount), nanos: Math.floor((topUpAmount % 1) * 1e9), currency };
      await machineService.topUpMachine({ session_id: session.id, amount, currency });
      toast({
        title: t('play.cabinet.topUpSuccess', { amount: topUpAmount, currency }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onTopUpClose();
      setTopUpAmount(10);
    } catch (error) {
      console.error('Failed to top up:', error);
      toast({ title: t('play.cabinet.topUpError'), status: 'error', duration: 3000, isClosable: true });
    }
  };

  const handleCashOut = async () => {
    if (!session?.id || cashOutAmount <= 0) return;
    try {
      const amount = { units: Math.floor(cashOutAmount), nanos: Math.floor((cashOutAmount % 1) * 1e9), currency };
      await machineService.cashOutMachine({ session_id: session.id, amount, currency });
      toast({
        title: t('play.cabinet.cashOutSuccess', { amount: cashOutAmount, currency }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onCashOutClose();
      setCashOutAmount(0);
    } catch (error) {
      console.error('Failed to cash out:', error);
      toast({ title: t('play.cabinet.cashOutError'), status: 'error', duration: 3000, isClosable: true });
    }
  };

  const sendButton = (buttonId: number, action: 'press' | 'release') => {
    if (!session) return;
    setPressedButtons((prev) => ({ ...prev, [buttonId]: action === 'press' }));
    inputStream.sendButtonEvent(buttonId, action === 'press' ? ButtonAction.PRESSED : ButtonAction.RELEASED);
  };

  const sendTouch = (e: React.PointerEvent<HTMLVideoElement>, action: TouchAction) => {
    if (!session) return;
    const target = e.currentTarget;
    const x = Math.floor((e.nativeEvent.offsetX * 0x7fff) / target.clientWidth);
    const y = Math.floor((e.nativeEvent.offsetY * 0x7fff) / target.clientHeight);
    inputStream.sendTouchEvent(x, y, action);
  };

  // Vertical control rail flanking the video in fullscreen (rows on mobile).
  const renderRail = (side: 'left' | 'right') => {
    const items =
      side === 'right'
        ? MAIN_BUTTONS.map((b) => ({ key: b.id, icon: b.icon, label: t(`play.cabinet.buttons.${b.key}`), color: b.color }))
        : [
            ...SERVICE_BUTTONS.map((b) => ({ key: b.id, icon: b.icon, label: t(`play.cabinet.buttons.${b.key}`), color: b.color })),
            ...LINE_IDS.map((id, i) => ({ key: id, text: String(i + 1), label: t('play.cabinet.line', { n: i + 1 }), color: 'green' })),
            ...BET_IDS.map((id, i) => ({ key: id, text: String(i + 1), label: t('play.cabinet.stake', { n: i + 1 }), color: 'yellow' })),
          ];
    return (
      <Flex
        direction={{ base: 'row', md: 'column' }}
        wrap="wrap"
        align="center"
        justify="center"
        gap={{ base: 2, md: 3 }}
        maxH={{ md: '100%' }}
        overflowY={{ md: 'auto' }}
        px={1}
        sx={{ '::-webkit-scrollbar': { display: 'none' } }}
      >
        {items.map((it) => (
          <CasinoButton
            key={it.key}
            icon={(it as { icon?: IconType }).icon}
            text={(it as { text?: string }).text}
            label={it.label}
            color={it.color}
            variant="service"
            isDisabled={!session}
            isPressed={!!pressedButtons[it.key]}
            onPress={() => sendButton(it.key, 'press')}
            onRelease={() => sendButton(it.key, 'release')}
          />
        ))}
      </Flex>
    );
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex justify="center" align="center" h="400px">
          <Spinner size="xl" thickness="4px" color="purple.400" />
        </Flex>
      </Container>
    );
  }

  if (!machine) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack spacing={4} align="stretch">
          <Button leftIcon={<FiArrowLeft />} variant="ghost" alignSelf="start" onClick={() => navigate('/play')}>
            {t('play.cabinet.back')}
          </Button>
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            {t('play.cabinet.notFound')}
          </Alert>
        </VStack>
      </Container>
    );
  }

  const unavailable = !isPlayable(machine.status) && !session;

  return (
    <Container maxW="container.xl" py={6}>
      <Global styles={HIDE_NATIVE_VIDEO_CONTROLS} />
      <Box
        position="relative"
        borderRadius="3xl"
        p={{ base: 4, md: 8 }}
        color="white"
        overflow="hidden"
        bgGradient="radial(120% 90% at 50% -10%, #3a1d63 0%, #1a0f33 45%, #0b0618 100%)"
        boxShadow="0 24px 70px rgba(0,0,0,0.55)"
        border="1px solid"
        borderColor="whiteAlpha.200"
      >
        {/* top marquee light */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          h="3px"
          bgGradient="linear(to-r, transparent, #ffd166, #ff4d8d, #8a5bff, transparent)"
          opacity={0.9}
        />

        <VStack spacing={{ base: 5, md: 7 }} align="stretch" position="relative">
          {/* Header */}
          <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
            <HStack spacing={3}>
              <IconButton
                aria-label={t('play.cabinet.back')}
                icon={<FiArrowLeft />}
                variant="ghost"
                color="whiteAlpha.900"
                _hover={{ bg: 'whiteAlpha.200' }}
                onClick={() => navigate('/play')}
              />
              <Icon as={GiRollingDices} boxSize={{ base: 7, md: 9 }} color="yellow.300" filter="drop-shadow(0 0 10px rgba(245,197,24,0.5))" />
              <VStack align="start" spacing={0}>
                <Heading
                  size={{ base: 'md', md: 'lg' }}
                  noOfLines={1}
                  bgGradient="linear(to-r, #ffd166, #ff8fb1, #b98bff)"
                  bgClip="text"
                  textShadow="0 0 24px rgba(185,139,255,0.35)"
                >
                  {machine.name || t('play.unnamed')}
                </Heading>
                {machine.category && (
                  <Text color="whiteAlpha.700" fontSize="sm" letterSpacing="0.08em" textTransform="uppercase">
                    {machine.category}
                  </Text>
                )}
              </VStack>
            </HStack>
            <HStack spacing={3}>
              {session && (
                <HStack
                  spacing={2}
                  px={3}
                  py={1.5}
                  borderRadius="full"
                  bg="blackAlpha.500"
                  border="1px solid"
                  borderColor={mainStream.status.connected ? 'red.400' : 'whiteAlpha.300'}
                >
                  <Box
                    w={2.5}
                    h={2.5}
                    borderRadius="full"
                    bg={mainStream.status.connected ? 'red.400' : 'gray.500'}
                    boxShadow={mainStream.status.connected ? '0 0 10px #f56565' : 'none'}
                    animation={mainStream.status.connected ? `${livePulse} 1.6s infinite` : 'none'}
                  />
                  <Text fontSize="xs" fontWeight="bold" letterSpacing="0.1em">
                    {mainStream.status.connected ? t('play.cabinet.live') : t('play.cabinet.connecting')}
                  </Text>
                </HStack>
              )}
              <Tooltip
                label={isFavorite(machine.id) ? t('play.removeFavorite') : t('play.addFavorite')}
              >
                <IconButton
                  aria-label={isFavorite(machine.id) ? t('play.removeFavorite') : t('play.addFavorite')}
                  icon={<FiStar fill={isFavorite(machine.id) ? 'currentColor' : 'none'} />}
                  variant="ghost"
                  color={isFavorite(machine.id) ? 'yellow.300' : 'whiteAlpha.900'}
                  _hover={{ bg: 'whiteAlpha.200' }}
                  onClick={() => toggleFavorite(machine.id)}
                />
              </Tooltip>
            </HStack>
          </Flex>

          {unavailable ? (
            <Alert status="warning" borderRadius="xl" bg="whiteAlpha.200" color="white">
              <AlertIcon color="orange.300" />
              <VStack align="start" spacing={0}>
                <Text fontWeight="bold">{t('play.cabinet.unavailable')}</Text>
                <Text fontSize="sm" color="whiteAlpha.800">
                  {t('play.cabinet.unavailableDesc')}
                </Text>
              </VStack>
            </Alert>
          ) : (
            <>
              <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6} alignItems="start">
                {/* Screens */}
                <GridItem>
                  <VStack spacing={4} align="stretch">
                    <Flex
                      ref={stageRef}
                      direction={isFullscreen ? { base: 'column', md: 'row' } : 'column'}
                      align="center"
                      justify="center"
                      gap={isFullscreen ? { base: 2, md: 4 } : 0}
                      {...(isFullscreen
                        ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1500, bg: 'black', p: { base: 2, md: 4 } }
                        : { position: 'relative', w: 'full' })}
                    >
                      {isFullscreen && session && renderRail('left')}

                      {/* Video frame — same DOM node in both modes (keeps the stream alive) */}
                      <Box
                        position="relative"
                        bg="black"
                        overflow="hidden"
                        w={isFullscreen ? { base: 'full', md: 'auto' } : 'full'}
                        h={isFullscreen ? { base: 'auto', md: 'full' } : undefined}
                        maxW="100%"
                        maxH="100%"
                        flex={isFullscreen ? '1 1 auto' : undefined}
                        minH={0}
                        borderRadius={isFullscreen ? 'lg' : '2xl'}
                        border="1px solid"
                        borderColor="whiteAlpha.200"
                        boxShadow={
                          isFullscreen
                            ? 'none'
                            : `0 0 44px ${isTouching ? 'rgba(56,224,240,0.5)' : 'rgba(138,43,226,0.35)'}, inset 0 0 0 2px rgba(255,255,255,0.06)`
                        }
                        sx={isFullscreen ? undefined : { aspectRatio: '16 / 9' }}
                      >
                        <video
                          id={MAIN_ELEMENT_ID}
                          autoPlay
                          muted
                          playsInline
                          controls={false}
                          disablePictureInPicture
                          controlsList="nodownload nofullscreen noremoteplayback"
                          onPointerDown={(e) => {
                            setIsTouching(true);
                            sendTouch(e, 'press');
                          }}
                          onPointerUp={(e) => {
                            setIsTouching(false);
                            sendTouch(e, 'release');
                          }}
                          onPointerLeave={(e) => {
                            if (isTouching) {
                              setIsTouching(false);
                              sendTouch(e, 'release');
                            }
                          }}
                          style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            background: '#000',
                            cursor: session ? 'crosshair' : 'default',
                            touchAction: 'none',
                          }}
                        />

                        {!session && (
                          <Flex
                            position="absolute"
                            inset={0}
                            bg="blackAlpha.700"
                            direction="column"
                            align="center"
                            justify="center"
                            gap={4}
                            p={6}
                            textAlign="center"
                          >
                            <Icon as={GiRollingDices} boxSize={16} color="yellow.300" filter="drop-shadow(0 0 16px rgba(245,197,24,0.5))" />
                            <Heading size="md">{t('play.cabinet.noSession')}</Heading>
                            <Text opacity={0.85} maxW="sm">
                              {t('play.cabinet.noSessionDesc')}
                            </Text>
                            <Button
                              leftIcon={<FiPlay />}
                              colorScheme="green"
                              size="lg"
                              onClick={handleStartSession}
                              isLoading={sessionLoading}
                            >
                              {t('play.cabinet.startSession')}
                            </Button>
                          </Flex>
                        )}

                        {session && (
                          <IconButton
                            aria-label={isFullscreen ? t('play.cabinet.exitFullscreen') : t('play.cabinet.fullscreen')}
                            icon={isFullscreen ? <FiMinimize /> : <FiMaximize />}
                            size="sm"
                            colorScheme="blackAlpha"
                            position="absolute"
                            top={2}
                            right={2}
                            onClick={() => setIsFullscreen((v) => !v)}
                          />
                        )}
                      </Box>

                      {isFullscreen && session && renderRail('right')}
                    </Flex>

                    {/* Second camera — overview (hideable) */}
                    {session && (
                      <Box>
                        <HStack w="full" align="center" spacing={3} py={1}>
                          <Box flex={1} h="1px" bgGradient="linear(to-r, transparent, whiteAlpha.400)" />
                          <Button
                            size="xs"
                            variant="ghost"
                            color="whiteAlpha.800"
                            _hover={{ bg: 'whiteAlpha.200' }}
                            leftIcon={showOverview ? <FiEyeOff /> : <FiEye />}
                            onClick={() => setShowOverview((v) => !v)}
                            letterSpacing="0.14em"
                            textTransform="uppercase"
                            fontSize="xs"
                          >
                            {showOverview ? t('play.cabinet.hideOverview') : t('play.cabinet.showOverview')}
                          </Button>
                          <Box flex={1} h="1px" bgGradient="linear(to-l, transparent, whiteAlpha.400)" />
                        </HStack>
                        <Collapse in={showOverview} animateOpacity>
                          <ScreenBezel glow="rgba(255,140,30,0.28)">
                            <AspectRatio ratio={16 / 9}>
                              <video
                                id={GENERAL_ELEMENT_ID}
                                autoPlay
                                muted
                                playsInline
                                controls={false}
                                disablePictureInPicture
                                controlsList="nodownload nofullscreen noremoteplayback"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#000' }}
                              />
                            </AspectRatio>
                            <HStack
                              position="absolute"
                              top={2}
                              left={2}
                              spacing={1.5}
                              bg="blackAlpha.600"
                              px={2}
                              py={1}
                              borderRadius="md"
                            >
                              <Box
                                w={2}
                                h={2}
                                borderRadius="full"
                                bg={generalStream.status.connected ? 'red.400' : 'gray.500'}
                              />
                              <Text fontSize="2xs" fontWeight="bold">
                                {generalStream.status.connected ? t('play.cabinet.live') : t('play.cabinet.connecting')}
                              </Text>
                            </HStack>
                          </ScreenBezel>
                        </Collapse>
                      </Box>
                    )}
                  </VStack>
                </GridItem>

                {/* Session / balance panel */}
                <GridItem>
                  <Box
                    borderRadius="2xl"
                    p={5}
                    bgGradient="linear(to-b, rgba(255,255,255,0.08), rgba(255,255,255,0.02))"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                  >
                    <Heading size="sm" mb={4} letterSpacing="0.08em" textTransform="uppercase" color="whiteAlpha.900">
                      {t('play.cabinet.sessionTitle')}
                    </Heading>
                    {session ? (
                      <VStack spacing={3} align="stretch">
                        <Button
                          leftIcon={<FiPlusCircle />}
                          size="lg"
                          color="#3a2400"
                          bgGradient="linear(to-b, #ffe08a, #f5b70a)"
                          _hover={{ bgGradient: 'linear(to-b, #ffe9a8, #ffc21f)' }}
                          _active={{ bgGradient: 'linear(to-b, #f5b70a, #d99e00)' }}
                          onClick={onTopUpOpen}
                        >
                          {t('play.cabinet.topUp')}
                        </Button>
                        <Button leftIcon={<FiDownloadCloud />} size="lg" colorScheme="green" onClick={onCashOutOpen}>
                          {t('play.cabinet.cashOut')}
                        </Button>
                        <Button
                          leftIcon={<FiXCircle />}
                          variant="outline"
                          color="red.200"
                          borderColor="red.400"
                          _hover={{ bg: 'red.500', color: 'white' }}
                          onClick={handleEndSession}
                          isLoading={sessionLoading}
                        >
                          {t('play.cabinet.endSession')}
                        </Button>
                      </VStack>
                    ) : (
                      <VStack spacing={3} align="stretch">
                        <Text fontSize="sm" color="whiteAlpha.800">
                          {t('play.cabinet.noSessionDesc')}
                        </Text>
                        <Button
                          leftIcon={<FiPlay />}
                          colorScheme="green"
                          size="lg"
                          onClick={handleStartSession}
                          isLoading={sessionLoading}
                        >
                          {t('play.cabinet.startSession')}
                        </Button>
                      </VStack>
                    )}
                  </Box>
                </GridItem>
              </Grid>

              {/* Control deck */}
              <Box
                borderRadius="2xl"
                p={{ base: 4, md: 6 }}
                bgGradient="linear(to-b, rgba(0,0,0,0.35), rgba(0,0,0,0.15))"
                border="1px solid"
                borderColor="whiteAlpha.200"
                boxShadow="inset 0 1px 0 rgba(255,255,255,0.06)"
              >
                <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                  {/* Main deck */}
                  <SimpleGrid columns={{ base: 3, md: 6 }} spacingX={{ base: 2, md: 4 }} spacingY={5} justifyItems="center">
                    {MAIN_BUTTONS.map((btn) => (
                      <CasinoButton
                        key={btn.id}
                        icon={btn.icon}
                        label={t(`play.cabinet.buttons.${btn.key}`)}
                        color={btn.color}
                        variant="main"
                        isDisabled={!session}
                        isPressed={!!pressedButtons[btn.id]}
                        onPress={() => sendButton(btn.id, 'press')}
                        onRelease={() => sendButton(btn.id, 'release')}
                      />
                    ))}
                  </SimpleGrid>

                  <SectionLabel>{t('play.cabinet.groups.service')}</SectionLabel>
                  <SimpleGrid columns={3} spacingX={{ base: 3, md: 4 }} spacingY={5} justifyItems="center" maxW="360px" mx="auto">
                    {SERVICE_BUTTONS.map((btn) => (
                      <CasinoButton
                        key={btn.id}
                        icon={btn.icon}
                        label={t(`play.cabinet.buttons.${btn.key}`)}
                        color={btn.color}
                        variant="service"
                        isDisabled={!session}
                        isPressed={!!pressedButtons[btn.id]}
                        onPress={() => sendButton(btn.id, 'press')}
                        onRelease={() => sendButton(btn.id, 'release')}
                      />
                    ))}
                  </SimpleGrid>

                  <SectionLabel>{t('play.cabinet.groups.lines')}</SectionLabel>
                  <SimpleGrid columns={5} spacingX={{ base: 1.5, md: 3 }} spacingY={4} justifyItems="center">
                    {LINE_IDS.map((id, i) => (
                      <CasinoButton
                        key={id}
                        text={String(i + 1)}
                        label={t('play.cabinet.line', { n: i + 1 })}
                        color="green"
                        variant="small"
                        isDisabled={!session}
                        isPressed={!!pressedButtons[id]}
                        onPress={() => sendButton(id, 'press')}
                        onRelease={() => sendButton(id, 'release')}
                      />
                    ))}
                  </SimpleGrid>

                  <SectionLabel>{t('play.cabinet.groups.bets')}</SectionLabel>
                  <SimpleGrid columns={5} spacingX={{ base: 1.5, md: 3 }} spacingY={4} justifyItems="center">
                    {BET_IDS.map((id, i) => (
                      <CasinoButton
                        key={id}
                        text={String(i + 1)}
                        label={t('play.cabinet.stake', { n: i + 1 })}
                        color="yellow"
                        variant="small"
                        isDisabled={!session}
                        isPressed={!!pressedButtons[id]}
                        onPress={() => sendButton(id, 'press')}
                        onRelease={() => sendButton(id, 'release')}
                      />
                    ))}
                  </SimpleGrid>

                  {!session && (
                    <Text fontSize="xs" color="whiteAlpha.700" textAlign="center">
                      {t('play.cabinet.controlsHint')}
                    </Text>
                  )}
                </VStack>
              </Box>
            </>
          )}
        </VStack>
      </Box>

      {/* Top up modal */}
      <Modal isOpen={isTopUpOpen} onClose={onTopUpClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="gray.900" color="white">
          <ModalHeader>{t('play.cabinet.topUp')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>
                  {t('play.cabinet.amount')}
                  {currency && (
                    <Text as="span" color="whiteAlpha.600" fontSize="sm" ml={2}>
                      ({t('play.cabinet.available')}: {getAvailableBalance(currency).toFixed(2)} {currency})
                    </Text>
                  )}
                </FormLabel>
                <NumberInput value={topUpAmount} onChange={(_, v) => setTopUpAmount(v || 0)} min={1} max={100000} step={1}>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel>{t('play.cabinet.currency')}</FormLabel>
                <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  {availableCurrencies.map((c) => (
                    <option key={c} value={c} style={{ color: '#000' }}>
                      {c}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onTopUpClose}>
              {t('play.cabinet.cancel')}
            </Button>
            <Button
              colorScheme="yellow"
              onClick={handleTopUp}
              isDisabled={!session || topUpAmount <= 0 || (!!currency && topUpAmount > getAvailableBalance(currency))}
            >
              {t('play.cabinet.confirm')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Cash out modal */}
      <Modal isOpen={isCashOutOpen} onClose={onCashOutClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="gray.900" color="white">
          <ModalHeader>{t('play.cabinet.cashOut')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>{t('play.cabinet.amount')}</FormLabel>
                <NumberInput value={cashOutAmount} onChange={(_, v) => setCashOutAmount(v || 0)} min={0} max={100000} step={1}>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel>{t('play.cabinet.currency')}</FormLabel>
                <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  {availableCurrencies.map((c) => (
                    <option key={c} value={c} style={{ color: '#000' }}>
                      {c}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCashOutClose}>
              {t('play.cabinet.cancel')}
            </Button>
            <Button colorScheme="green" onClick={handleCashOut} isDisabled={cashOutAmount <= 0}>
              {t('play.cabinet.confirm')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};
